import { Button } from '@mui/material'
import { createSelector } from '@reduxjs/toolkit'
import { useCallback } from 'react'
import { RootState } from '../../../store'
import { BuildingEditingTool } from '../../../models/Construction'
import { validate as isValidUUID } from 'uuid'
import { SceneManager } from '../../../visualization/sceneManager'
import { isWallMesh } from '../../../visualization/utils'
import { useSelector } from 'react-redux'
import { EventType } from '../../../utils/EventEmitter'

const selectSnappableMeshes = createSelector(
  [
    (state: RootState) => state.construction.selectedTool,
    (state: RootState) => state.construction.selectedElementsIds,
  ],
  (tool, elementsIds) => {
    const firstElementId = elementsIds[0]
    const secondElementId = elementsIds[1]
    if (
      tool !== BuildingEditingTool.Marquee ||
      !firstElementId ||
      !isValidUUID(firstElementId) ||
      !secondElementId ||
      !isValidUUID(secondElementId)
    ) {
      return [null, null]
    }

    const scene = SceneManager.getInstance().getScene()
    const firstMesh = scene?.getMeshById(firstElementId)
    const secondMesh = scene?.getMeshById(secondElementId)

    if (
      firstMesh &&
      isWallMesh(firstMesh) &&
      secondMesh &&
      isWallMesh(secondMesh)
    ) {
      return [firstMesh, secondMesh]
    }

    return [null, null]
  },
)

const ObjectSnapButton = () => {
  const [firstMesh, secondMesh] = useSelector(selectSnappableMeshes)

  const handleSnapButtonClick = useCallback(() => {
    if (!firstMesh || !secondMesh) {
      return
    }

    const eventEmitter = SceneManager.getInstance().getEventEmitter()
    eventEmitter?.emit(EventType.BuildingSnapWallElements, {
      firstMesh,
      secondMesh,
    })
  }, [firstMesh, secondMesh])

  if (!firstMesh || !secondMesh) {
    return null
  }

  const component = (
    <Button
      disableRipple
      size='small'
      onClick={handleSnapButtonClick}
      sx={{
        textTransform: 'none',
        background: '#ffffff',
        color: '#04002E',
        fontSize: '0.76rem',
        width: '100%',
      }}
    >
      Snap Objects
    </Button>
  )

  return {
    component,
    meshes: [firstMesh, secondMesh],
  }
}

export default ObjectSnapButton
